import { RedCards } from 'components/RedCards/RedCards';
import {
  BasketballLiveEvent,
  LiveEventBet,
  LiveEvent as LiveEventDto,
  LiveEventOdd,
  TennisLiveEvent,
} from 'types/LiveEvent';
import styles from './LiveEvent.module.css';
import { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import {
  getBasketballHandicapBet,
  getBasketballOverUnderBet,
  getRestOfMatchBet,
  getFullTimeOdds,
  getGoalsOverUnderBet,
  getNextGoalBet,
  getSetWinnerBet,
  getTennisOverUnderBet,
} from 'helpers/matchHelper';
import { Tooltip } from 'react-tooltip';
import {
  HIGHLIGHT_ODDS_CHANGE_TIME,
  LIVE_FULL_TIME_BET_MAP,
  TOOLTIP_DELAY_SHOW,
} from 'config/constants';
import { LiveBettingContext } from 'context/liveBettingContext';
import { CurrentMatchFlow } from '../CurrentMatchFlow/CurrentMatchFlow';
import { CssLabels, SportIDs } from 'types/enums';
import { SearchableOtherBets } from 'components/OtherBets/SearchableOtherBets';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { useTranslation } from 'react-i18next';
import { liveClock } from 'utils/sportEvent.utils';
import { useModernDesktop } from 'components/Layout/ModernDesktopShell';
import {
  LiveMarket,
  livePickLabel,
} from 'components/mobile/LiveBetting/liveMarkets';
import {
  isSuspendedOdd,
  SuspendedLock,
} from 'components/SuspendedLock/SuspendedLock';

interface Props {
  event: LiveEventDto;
  isFavorite?: boolean;
  oddRow?: boolean;
  /** Prematch upcoming list: sport icon between names, no 0:0 placeholder. */
  prematchUpcoming?: boolean;
  /** Modern: the odds the row shows instead of 1 X 2 (double chance...). */
  market?: LiveMarket;
}

function liveEventOddsSignature(ev: LiveEventDto | null | undefined): string {
  if (!ev) return '';
  const ft = getFullTimeOdds(ev);
  const parts: string[] = [];
  if (ft.homeWin) parts.push(`FT:${ft.homeWin.custom_id}:${ft.homeWin.odds}`);
  if (ft.draw) parts.push(`FT:${ft.draw.custom_id}:${ft.draw.odds}`);
  if (ft.awayWin) parts.push(`FT:${ft.awayWin.custom_id}:${ft.awayWin.odds}`);
  ev.bets?.forEach((b) => {
    b.odds?.forEach((o) => {
      parts.push(`${b.name}:${o.custom_id}:${o.odds}`);
    });
  });
  return parts.join('|');
}

export const LiveEvent = ({
  event,
  isFavorite: inFavoritesList = false,
  oddRow = false,
  prematchUpcoming = false,
  market,
}: Props) => {
  // Each moved price keeps its own up/down mark until it expires. The site
  // gets a sport's events one by one as they arrive, and every arrival
  // replaces the "previous" snapshot - comparing against it alone cancelled
  // the animation of a price that had moved a moment earlier.
  const [changeMarks, setChangeMarks] = useState<
    Record<string, { label: CssLabels; at: number }>
  >({});
  const [selectedBets, setSelectedBets] = useState<
    {
      bet: LiveEventBet;
      odd: LiveEventOdd;
    }[]
  >([]);

  const [showOtherBets, setShowOtherBets] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const { t } = useTranslation();

  const { entries, addSlipEntry, removeSlipEntry, entryExist } =
    useContext(SlipContext);
  const {
    addFavorite,
    removeFavorite,
    getLastOddsForEvent,
    isFavorite: isFavoriteEvent,
  } = useContext(LiveBettingContext);
  // A favourite stays in its sport's list too, its star filled there as well.
  const isFavorite = inFavoritesList || isFavoriteEvent(event);

  const prevEventRow = getLastOddsForEvent(event) ?? null;

  const { homeWin, draw, awayWin } = getFullTimeOdds(event);

  const currentOddsSignature = useMemo(
    () => liveEventOddsSignature(event),
    [event]
  );
  const prevOddsSignature = useMemo(
    () => liveEventOddsSignature(prevEventRow),
    [prevEventRow]
  );

  useEffect(() => {
    if (!prevEventRow) return;
    if (currentOddsSignature === prevOddsSignature) return;
    const at = Date.now();
    const moved: Record<string, { label: CssLabels; at: number }> = {};
    const allBets = [event.fullTimeBet, ...(event.bets ?? [])].filter(Boolean);
    for (const bet of allBets) {
      for (const odd of bet.odds ?? []) {
        const label = getDataChangedCssValue(bet.name, odd.custom_id);
        if (label !== CssLabels.SameValue) {
          moved[odd.custom_id] = { label, at };
        }
      }
    }
    if (Object.keys(moved).length === 0) return;
    setChangeMarks((marks) => ({ ...marks, ...moved }));
    const t = setTimeout(() => {
      setChangeMarks((marks) => {
        const next = { ...marks };
        for (const id of Object.keys(moved)) {
          // A later move of the same price has its own timer.
          if (next[id]?.at === at) delete next[id];
        }
        return next;
      });
    }, HIGHLIGHT_ODDS_CHANGE_TIME);
    return () => clearTimeout(t);
    // Runs when this event's own prices change, not on other events' arrivals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOddsSignature]);

  const changeMark = (oddId: string) => changeMarks[oddId]?.label ?? '';
  // Remounting on each new mark restarts the flash for a price that moves again.
  const markKey = (oddId: string) => `${oddId}-${changeMarks[oddId]?.at ?? 0}`;

  // Since we extracted some data from soccer event
  // We need to check if any odd is selected for full time, next goal, over/under bets
  useEffect(() => {
    const oddsToSelect: { bet: LiveEventBet; odd: LiveEventOdd }[] = [];

    const markOddsAsSelected = (bet: LiveEventBet | null | undefined) => {
      bet?.odds.forEach((x) => {
        if (entryExist(event, bet, x)) {
          oddsToSelect.push({ bet: bet, odd: x });
        }
      });
    };

    markOddsAsSelected(event.fullTimeBet);

    if (event.sportId === SportIDs.SOCCER) {
      markOddsAsSelected(getRestOfMatchBet(event));
      markOddsAsSelected(getNextGoalBet(event));
      markOddsAsSelected(getGoalsOverUnderBet(event));
    } else if (event.sportId === SportIDs.BASKETBALL) {
      markOddsAsSelected(getBasketballHandicapBet(event));
      markOddsAsSelected(getBasketballOverUnderBet(event));
    } else if (event.sportId === SportIDs.TENNIS) {
      const tennisEvent = event as TennisLiveEvent;
      markOddsAsSelected(getSetWinnerBet(tennisEvent));
      markOddsAsSelected(getTennisOverUnderBet(tennisEvent));
    }

    setSelectedBets(oddsToSelect);
  }, [entryExist, event]);

  const handleFavoriteClick = () => {
    if (!isFavorite) {
      addFavorite(event);
    } else {
      removeFavorite(event);
    }
  };

  useEffect(() => {
    const toDeleteIndexes: number[] = [];
    selectedBets.forEach(
      (x: { bet: LiveEventBet; odd: LiveEventOdd }, index: number) => {
        if (!entryExist(event, x.bet, x.odd)) {
          toDeleteIndexes.push(index);
        }
      }
    );

    if (toDeleteIndexes.length > 0) {
      const selectedCopy = [...selectedBets];
      for (let i = toDeleteIndexes.length - 1; i >= 0; --i) {
        selectedCopy.splice(toDeleteIndexes[i], 1);
      }

      setSelectedBets(selectedCopy);
    }
  }, [entries, entryExist, event, selectedBets]);

  const handleOddClick = (bet: LiveEventBet, odd: LiveEventOdd) => {
    const selectedBetsCopy = [...selectedBets];
    const index = selectedBetsCopy.findIndex(
      (x) => x.odd.custom_id === odd.custom_id
    );
    // Suspended by bet365 for the moment: it cannot be picked (a picked
    // one can still be taken off).
    if (index < 0 && isSuspendedOdd(odd)) return;

    if (index > -1) {
      selectedBetsCopy.splice(index, 1);
      removeSlipEntry(event, bet, odd, true);
    } else {
      selectedBetsCopy.push({ bet, odd });
      addSlipEntry(event, bet, odd, true);
    }

    setSelectedBets(selectedBetsCopy);
  };

  const getDataChangedCssValue = (
    betName: string,
    oddId: string
  ): CssLabels => {
    let cssLabel: CssLabels = CssLabels.SameValue;

    if (prevEventRow) {
      if (betName === 'Full Time') {
        const prevFullTimeData = getFullTimeOdds(prevEventRow);
        if (oddId === homeWin?.custom_id) {
          if (prevFullTimeData.homeWin?.odds > homeWin?.odds) {
            cssLabel = CssLabels.LowerValue;
          } else if (prevFullTimeData.homeWin?.odds < homeWin?.odds) {
            cssLabel = CssLabels.HigherValue;
          }
        }
        if (oddId === draw?.custom_id) {
          if (prevFullTimeData.draw?.odds > draw?.odds) {
            cssLabel = CssLabels.LowerValue;
          } else if (prevFullTimeData.draw?.odds < draw?.odds) {
            cssLabel = CssLabels.HigherValue;
          }
        } else if (oddId === awayWin?.custom_id) {
          if (prevFullTimeData.awayWin?.odds > awayWin?.odds) {
            cssLabel = CssLabels.LowerValue;
          } else if (prevFullTimeData.awayWin?.odds < awayWin?.odds) {
            cssLabel = CssLabels.HigherValue;
          }
        }
      } else {
        const prevBet = prevEventRow.bets.find(
          (x: LiveEventBet) => x.name === betName
        );
        const bet = event.bets.find((x: LiveEventBet) => x.name === betName);
        if (prevBet && bet) {
          const prevOdd = prevBet.odds.find(
            (x: LiveEventBet) => x.custom_id === oddId
          );
          const odd = bet.odds.find((x) => x.custom_id === oddId);

          if (prevOdd && odd) {
            if (prevOdd.odds > odd.odds) {
              cssLabel = CssLabels.LowerValue;
            } else if (prevOdd.odds < odd.odds) {
              cssLabel = CssLabels.HigherValue;
            }
          }
        }
      }
    }

    return cssLabel;
  };

  const showBetOdds = (bet: LiveEventBet, showMargin?: boolean) => {
    const handicapToShow = bet?.odds?.find((x) => !!x.handicap)?.handicap || '';
    return (
      <>
        {bet?.odds && bet.odds.length > 0 && (
          <>
            {showMargin && (
              <div className={styles.showMargin}>{handicapToShow}</div>
            )}
            {bet.odds.map((x) => (
              <div
                key={markKey(x.custom_id)}
                data-changed={changeMark(x.custom_id)}
                data-selected={
                  selectedBets.findIndex(
                    (y) => x.custom_id === y.odd.custom_id
                  ) > -1
                }
                className={classNames(styles.odd)}
                onClick={() => handleOddClick(bet, x)}
              >
                {isSuspendedOdd(x) ? <SuspendedLock /> : x.odds.toFixed(2)}
              </div>
            ))}
          </>
        )}
      </>
    );
  };

  const getRestOfColumns = (event: LiveEventDto) => {
    switch (event.sportId) {
      case SportIDs.SOCCER:
        const restOfMatchBet = getRestOfMatchBet(event);
        const nextGoalBet = getNextGoalBet(event);
        const soccerOverUnder = getGoalsOverUnderBet(event);
        return (
          <>
            <div className={classNames(styles.column, styles.bet)}>
              {restOfMatchBet?.odds &&
                restOfMatchBet.odds.length > 0 &&
                showBetOdds(restOfMatchBet)}
            </div>
            <div className={classNames(styles.column, styles.bet)}>
              {nextGoalBet?.odds &&
                nextGoalBet.odds.length > 0 &&
                showBetOdds(nextGoalBet)}
            </div>
            <div className={classNames(styles.column, styles.bet)}>
              {soccerOverUnder?.odds &&
                soccerOverUnder.odds.length > 0 &&
                showBetOdds(soccerOverUnder, true)}
            </div>
          </>
        );

      case SportIDs.TENNIS:
        const tennisEvent: TennisLiveEvent = event as TennisLiveEvent;
        const setWinnerBet = getSetWinnerBet(tennisEvent);
        const tennisOverUnder = getTennisOverUnderBet(tennisEvent);
        const showHomeServeIcon = tennisEvent.currentServe === 'home';
        const showAwayServeIcon = tennisEvent.currentServe === 'away';
        return (
          <>
            <div className={classNames(styles.column, styles.bet)}>
              {setWinnerBet?.odds &&
                setWinnerBet.odds.length > 0 &&
                showBetOdds(setWinnerBet)}
            </div>
            <div className={styles.column}>
              <div className={styles.tennisCurrentResult}>
                <span
                  className={styles.serveBallIcon}
                  data-active={showHomeServeIcon}
                >
                  <SportIcon
                    sportId={SportIDs.TENNIS}
                    alt="Serving"
                    className={styles.serveBallIcon}
                  />
                </span>
                <span className={styles.tennisSetResult}>
                  {tennisEvent.currentSetResult}
                </span>
                <span className={styles.tennisGameResult}>
                  {tennisEvent.currentGameResult}
                </span>
                <span
                  className={styles.serveBallIcon}
                  data-active={showAwayServeIcon}
                >
                  <SportIcon
                    sportId={SportIDs.TENNIS}
                    alt="Serving"
                    className={styles.serveBallIcon}
                  />
                </span>
              </div>
            </div>
            <div className={classNames(styles.column, styles.bet)}>
              {tennisOverUnder?.odds &&
                tennisOverUnder.odds.length > 0 &&
                showBetOdds(tennisOverUnder, true)}
            </div>
          </>
        );

      case SportIDs.BASKETBALL:
        const basketballEvent: BasketballLiveEvent =
          event as BasketballLiveEvent;
        const basketballOverUnder = getBasketballOverUnderBet(basketballEvent);
        const basketballHandicap = getBasketballHandicapBet(basketballEvent);
        return (
          <>
            <div className={styles.column}></div>
            <div className={classNames(styles.column, styles.bet)}>
              {basketballHandicap?.odds &&
                basketballHandicap.odds.length > 0 &&
                showBetOdds(basketballHandicap, true)}
            </div>
            <div className={classNames(styles.column, styles.bet)}>
              {basketballOverUnder?.odds &&
                basketballOverUnder.odds.length > 0 &&
                showBetOdds(basketballOverUnder, true)}
            </div>
          </>
        );

      default:
        // Same width as soccer/basketball grid so row borders and header align when
        // this sport has no dedicated markets (rugby, futsal, 2-way-only, etc.).
        return (
          <>
            <div className={styles.column} aria-hidden />
            <div className={styles.column} aria-hidden />
            <div className={styles.column} aria-hidden />
          </>
        );
    }
  };

  const betsToShow = event.bets.filter(
    (x) => x.name !== LIVE_FULL_TIME_BET_MAP.get(event.sportId)
  );

  const modernDesktop = useModernDesktop();
  if (modernDesktop) {
    const otherMarket = market && market.key !== 'full' ? market : undefined;
    const otherBet = otherMarket?.pick?.(event);
    const line = otherMarket?.totals
      ? (otherBet?.odds.find((x) => !!x.handicap)?.handicap ?? '').replace(
          /^[+-]/,
          ''
        )
      : '';
    const picks = otherMarket
      ? (otherBet?.odds ?? []).map((odd) => ({
          key: livePickLabel(event, odd, !!otherMarket.totals, t),
          odd,
          bet: otherBet!,
        }))
      : homeWin && awayWin
        ? [
            { key: '1', odd: homeWin, bet: event.fullTimeBet },
            { key: 'X', odd: draw, bet: event.fullTimeBet },
            { key: '2', odd: awayWin, bet: event.fullTimeBet },
          ].filter((p) => p.odd)
        : [];
    return (
      <>
        {showInsights && (
          <MatchInsightsModal
            fi={event.fi}
            onClose={() => setShowInsights(false)}
            liveOdds={<SearchableOtherBets match={event} bets={event.bets} />}
          />
        )}
        <div className={styles.modernCard}>
          <div className={styles.modernLeague}>
            <SportIcon
              className={styles.modernSport}
              sportId={event.sportId}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
              alt=""
            />
            <span>{event.league}</span>
          </div>
          <div className={styles.modernRow}>
            <div className={styles.modernTime}>
              <span>{liveClock(event, t)}</span>
              {!prematchUpcoming && <em>Live</em>}
            </div>
            <div className={styles.modernTeams}>
              <div className={styles.modernTeam}>
                <span>{event.home.name}</span>
                <RedCards
                  count={event.homeRedCards}
                  yellow={event.liveCounts?.yellow?.[0]}
                />
              </div>
              <div className={styles.modernTeam}>
                <span>{event.away.name}</span>
                <RedCards
                  count={event.awayRedCards}
                  yellow={event.liveCounts?.yellow?.[1]}
                />
              </div>
            </div>
            {!prematchUpcoming && (
              <div className={styles.modernScore}>
                <span>{event.homeGoals}</span>
                <span>{event.awayGoals}</span>
              </div>
            )}
            <div className={styles.modernOdds}>
              {line && <span className={styles.modernLine}>{line}</span>}
              {picks.length === 0 && (
                <span className={styles.modernLine}>
                  {t('no_bet_offer_message')}
                </span>
              )}
              {picks.map((p) => (
                <div
                  key={`${p.key}-${markKey(p.odd!.custom_id)}`}
                  data-changed={changeMark(p.odd!.custom_id)}
                  data-selected={
                    selectedBets.findIndex(
                      (y) => p.odd!.custom_id === y.odd.custom_id
                    ) > -1
                  }
                  className={styles.odd}
                  onClick={() => handleOddClick(p.bet, p.odd!)}
                >
                  <small>{p.key}</small>
                  {isSuspendedOdd(p.odd) ? (
                    <SuspendedLock />
                  ) : (
                    p.odd!.odds.toFixed(2)
                  )}
                </div>
              ))}
            </div>
            <div className={styles.modernActions}>
              <ThemeIcon
                icon="star"
                active={isFavorite}
                alt="Favorite"
                variant="inline"
                className={styles.favoriteIcon}
                onClick={handleFavoriteClick}
              />
              <ThemeIcon
                icon="stats-on"
                alt={t('match_statistics_label')}
                title={t('match_statistics_label')}
                variant="inline"
                className={styles.favoriteIcon}
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInsights(true);
                }}
              />
              <button
                type="button"
                className={styles.modernMore}
                onClick={() => setShowOtherBets(!showOtherBets)}
              >
                +{betsToShow.length} {showOtherBets ? '⌃' : '›'}
              </button>
            </div>
          </div>
          {showOtherBets && (
            <div className={styles.modernOther}>
              <SearchableOtherBets bets={betsToShow} match={event} />
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {showInsights && (
        <MatchInsightsModal
          fi={event.fi}
          onClose={() => setShowInsights(false)}
          liveOdds={<SearchableOtherBets match={event} bets={event.bets} />}
        />
      )}
      <div className={styles.wrapper} data-is-odd-row={oddRow}>
        <div
          className={styles.time}
          data-tooltip-id={`${event.fi}-time`}
          data-tooltip-place="left"
        >
          {liveClock(event, t)}
        </div>
        {event.resultPerPeriod && event.resultPerPeriod.length > 0 && (
          <Tooltip
            id={`${event.fi}-time`}
            delayShow={TOOLTIP_DELAY_SHOW}
            className={classNames('tooltip', styles.currentResult)}
            arrowColor="#1678c3"
          >
            <CurrentMatchFlow results={event.resultPerPeriod} />
          </Tooltip>
        )}
        <div
          className={styles.teamsAndResult}
          data-tooltip-id={event.fi}
          data-tooltip-content={`${event.league}:  ${event.home.name} - ${event.away.name}`}
          data-tooltip-place="bottom"
        >
          <ThemeIcon
            icon="star"
            active={isFavorite}
            alt="Favorite"
            variant="inline"
            className={styles.favoriteIcon}
            onClick={handleFavoriteClick}
          />
          <ThemeIcon
            icon="stats-on"
            alt={t('match_statistics_label')}
            title={t('match_statistics_label')}
            variant="inline"
            className={styles.favoriteIcon}
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowInsights(true);
            }}
          />
          {!prematchUpcoming && (
            <SportIcon
              className={styles.sportIcon}
              sportId={event.sportId}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
              alt={`Sport ${event.sportId}`}
            />
          )}
          <span>
            {event.home.name}
            <RedCards
              count={event.homeRedCards}
              yellow={event.liveCounts?.yellow?.[0]}
            />
          </span>
          {prematchUpcoming ? (
            <div className={styles.sportLogoSlot}>
              <SportIcon
                className={styles.sportLogoCenter}
                sportId={event.sportId}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getDefaultSportIconSrc();
                }}
                alt=""
              />
            </div>
          ) : (
            <span className={styles.result}>
              {event.homeGoals}:{event.awayGoals}
            </span>
          )}
          <span>
            {event.away.name}
            <RedCards
              count={event.awayRedCards}
              yellow={event.liveCounts?.yellow?.[1]}
            />
          </span>
        </div>
        <Tooltip
          id={event.fi}
          delayShow={TOOLTIP_DELAY_SHOW}
          className="tooltip"
          arrowColor="#1678c3"
        />
        {market && market.key !== 'full' ? (
          // The market picked over the first column (classic desktop).
          <div className={classNames(styles.column, styles.bet)}>
            {(() => {
              const bet = market.pick?.(event);
              return bet?.odds?.length
                ? showBetOdds(bet, !!market.totals)
                : null;
            })()}
          </div>
        ) : (
          <div className={classNames(styles.column, styles.bet)}>
            {homeWin && awayWin && (
              <>
                <div
                  key={markKey(homeWin.custom_id)}
                  data-changed={changeMark(homeWin.custom_id)}
                  data-selected={
                    selectedBets.findIndex(
                      (y) => homeWin.custom_id === y.odd.custom_id
                    ) > -1
                  }
                  className={classNames(styles.odd)}
                  onClick={() => handleOddClick(event.fullTimeBet, homeWin)}
                >
                  {isSuspendedOdd(homeWin) ? (
                    <SuspendedLock />
                  ) : (
                    homeWin.odds.toFixed(2)
                  )}
                </div>
                {draw && (
                  <div
                    key={markKey(draw.custom_id)}
                    data-changed={changeMark(draw.custom_id)}
                    data-selected={
                      selectedBets.findIndex(
                        (y) => draw.custom_id === y.odd.custom_id
                      ) > -1
                    }
                    className={classNames(styles.odd)}
                    onClick={() => handleOddClick(event.fullTimeBet, draw)}
                  >
                    {isSuspendedOdd(draw) ? (
                      <SuspendedLock />
                    ) : (
                      draw.odds.toFixed(2)
                    )}
                  </div>
                )}
                <div
                  key={markKey(awayWin.custom_id)}
                  data-changed={changeMark(awayWin.custom_id)}
                  data-selected={
                    selectedBets.findIndex(
                      (y) => awayWin.custom_id === y.odd.custom_id
                    ) > -1
                  }
                  className={classNames(styles.odd)}
                  onClick={() => handleOddClick(event.fullTimeBet, awayWin)}
                >
                  {isSuspendedOdd(awayWin) ? (
                    <SuspendedLock />
                  ) : (
                    awayWin.odds.toFixed(2)
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {getRestOfColumns(event)}
        <div
          className={styles.showMore}
          onClick={() => setShowOtherBets(!showOtherBets)}
        >
          +{betsToShow.length}
          <ThemeIcon icon="chevron-right-blue" alt="More" variant="inline" />
        </div>
      </div>
      {showOtherBets && <SearchableOtherBets bets={betsToShow} match={event} />}
    </>
  );
};
